"use client";

import { c as _c } from "react/compiler-runtime";
import { AudioLines, ChevronUp, CreditCard, Edit2, ExternalLink, LogOut, MoreVerticalIcon, Trash2 } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { SidebarLoungePoints } from "@/components/lounge/sidebar-points";
import { ProductSwitcher } from "@/components/product-switcher";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuAction, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useOrganization } from "@/hooks/useOrganization";
import { useDeleteAudioHistory, useRenameAudioHistory } from "@/hooks/usePlaygroundHistory";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { HistorySkeleton } from "./history-skeleton";
import { OrganizationSwitcher } from "./organization-switcher";
import { SidebarChatSearch, SidebarNewAction } from "./sidebar-actions";
import { StudioNav } from "./studio-nav";
import type { AudioGalleryItem } from "@/lib/audio-gen";
import type { Organization } from "@/lib/types";
interface AudioSidebarProps {
  galleryItems: AudioGalleryItem[];
  isHistoryLoading?: boolean;
  onNewChat: () => void;
  onItemClick: (itemId: string) => void;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  currentItemId?: string | null;
  className?: string;
}
type AudioHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "item";
  key: string;
  item: AudioGalleryItem;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 28;
const ROW_HEIGHT_SPACER = 6;
const ROW_HEIGHT_ITEM = 60;
interface AudioHistoryRowProps {
  rows: AudioHistoryRow[];
  currentItemId?: string | null;
  editingId: string | null;
  editPrompt: string;
  pendingFocusId: string | null;
  onItemClick: (itemId: string) => void;
  onEditPromptChange: (value: string) => void;
  onSaveEdit: (id: string, original: string) => void;
  onCancelEdit: () => void;
  onDeleteItem: (id: string) => void;
  onStartEdit: (id: string, prompt: string) => void;
  onEditFocused: () => void;
}
function getAudioHistoryRowHeight(index: number, {
  rows
}: AudioHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_ITEM;
}
function EditAudioPromptInput(t0) {
  const $ = _c(17);
  const {
    itemId,
    value,
    original,
    shouldFocus,
    onChange,
    onSave,
    onCancel,
    onFocused
  } = t0;
  const inputRef = useRef(null);
  const canceledRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== onFocused || $[1] !== shouldFocus) {
    t1 = () => {
      if (!shouldFocus || !inputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      onFocused();
    };
    t2 = [shouldFocus, onFocused];
    $[0] = onFocused;
    $[1] = shouldFocus;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== itemId || $[7] !== onSave || $[8] !== original) {
    t4 = () => {
      if (!canceledRef.current) {
        onSave(itemId, original);
      }
      canceledRef.current = false;
    };
    $[6] = itemId;
    $[7] = onSave;
    $[8] = original;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== onCancel) {
    t5 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        e_0.currentTarget.blur();
      }
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        canceledRef.current = true;
        onCancel();
      }
    };
    $[10] = onCancel;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== value) {
    t6 = <Input ref={inputRef} value={value} onChange={t3} onBlur={t4} onKeyDown={t5} className="h-7 text-sm border-none px-1 focus-visible:ring-0 bg-transparent" />;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = value;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function AudioHistoryRowComponent(t0) {
  const $ = _c(37);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentItemId,
    editingId,
    editPrompt,
    pendingFocusId,
    onItemClick,
    onEditPromptChange,
    onSaveEdit,
    onCancelEdit,
    onDeleteItem,
    onStartEdit,
    onEditFocused
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const {
    item
  } = row;
  const isEditing = editingId === item.id;
  const isActive = currentItemId === item.id;
  let t1;
  if ($[8] !== item.models) {
    t1 = item.models.every(_temp);
    $[8] = item.models;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const isSaved = t1;
  let t2;
  if ($[10] !== editPrompt || $[11] !== isActive || $[12] !== isEditing || $[13] !== item.id || $[14] !== item.prompt || $[15] !== item.timestamp || $[16] !== onCancelEdit || $[17] !== onEditFocused || $[18] !== onEditPromptChange || $[19] !== onItemClick || $[20] !== onSaveEdit || $[21] !== pendingFocusId) {
    t2 = isEditing ? <div className="flex h-full w-full items-center rounded-md px-2 pr-8 text-left text-sm ring-sidebar-ring bg-sidebar-accent text-sidebar-accent-foreground"><EditAudioPromptInput itemId={item.id} value={editPrompt} original={item.prompt} shouldFocus={pendingFocusId === item.id} onChange={onEditPromptChange} onSave={onSaveEdit} onCancel={onCancelEdit} onFocused={onEditFocused} /></div> : <SidebarMenuButton onClick={() => onItemClick(item.id)} isActive={isActive} className="h-full! w-full justify-start group relative pr-2 !transition-none group-hover/audio-row:pr-9" type="button"><div className="flex items-start gap-2 min-w-0 w-full"><div className="flex h-8 w-8 shrink-0 items-center justify-center rounded border mt-0.5"><AudioLines className="h-4 w-4 text-muted-foreground" /></div><div className="flex-1 min-w-0"><div className="truncate text-sm font-medium mb-0.5">{item.prompt}</div><div className="text-xs text-muted-foreground">{new Date(item.timestamp).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit"
            })}</div></div></div></SidebarMenuButton>;
    $[10] = editPrompt;
    $[11] = isActive;
    $[12] = isEditing;
    $[13] = item.id;
    $[14] = item.prompt;
    $[15] = item.timestamp;
    $[16] = onCancelEdit;
    $[17] = onEditFocused;
    $[18] = onEditPromptChange;
    $[19] = onItemClick;
    $[20] = onSaveEdit;
    $[21] = pendingFocusId;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== isEditing || $[24] !== isSaved || $[25] !== item.id || $[26] !== item.prompt || $[27] !== onDeleteItem || $[28] !== onStartEdit) {
    t3 = !isEditing && isSaved && <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"><DropdownMenu><DropdownMenuTrigger asChild={true}><SidebarMenuAction type="button" onClick={_temp2} className="pointer-events-none static hidden h-7 w-7 cursor-pointer opacity-0 group-hover/audio-row:flex group-hover/audio-row:pointer-events-auto group-hover/audio-row:opacity-100 data-[state=open]:flex data-[state=open]:pointer-events-auto data-[state=open]:opacity-100"><MoreVerticalIcon className="h-3.5 w-3.5" /></SidebarMenuAction></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-40"><DropdownMenuItem onClick={e_0 => {
            e_0.stopPropagation();
            onStartEdit(item.id, item.prompt);
          }} className="flex items-center gap-2"><Edit2 className="h-4 w-4" />Rename</DropdownMenuItem><DropdownMenuItem onClick={e_1 => {
            e_1.stopPropagation();
            onDeleteItem(item.id);
          }} className="flex items-center gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Delete</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>;
    $[23] = isEditing;
    $[24] = isSaved;
    $[25] = item.id;
    $[26] = item.prompt;
    $[27] = onDeleteItem;
    $[28] = onStartEdit;
    $[29] = t3;
  } else {
    t3 = $[29];
  }
  let t4;
  if ($[30] !== t2 || $[31] !== t3) {
    t4 = <div className="relative h-full px-2 pb-1"><div className="group/audio-row relative h-full">{t2}{t3}</div></div>;
    $[30] = t2;
    $[31] = t3;
    $[32] = t4;
  } else {
    t4 = $[32];
  }
  let t5;
  if ($[33] !== ariaAttributes || $[34] !== style || $[35] !== t4) {
    t5 = <div {...ariaAttributes} style={style}>{t4}</div>;
    $[33] = ariaAttributes;
    $[34] = style;
    $[35] = t4;
    $[36] = t5;
  } else {
    t5 = $[36];
  }
  return t5;
}
function _temp2(e) {
  e.stopPropagation();
}
function _temp(m) {
  return !m.isLoading;
}
function groupItemsByDate(items: AudioGalleryItem[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as AudioGalleryItem[],
    yesterday: [] as AudioGalleryItem[],
    lastWeek: [] as AudioGalleryItem[],
    older: [] as AudioGalleryItem[]
  };
  items.forEach(item => {
    const itemDate = new Date(item.timestamp);
    if (itemDate.toDateString() === today.toDateString()) {
      groups.today.push(item);
    } else if (itemDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(item);
    } else if (itemDate >= lastWeek) {
      groups.lastWeek.push(item);
    } else {
      groups.older.push(item);
    }
  });
  return groups;
}
export function AudioSidebar(t0) {
  const $ = _c(111);
  const {
    galleryItems,
    isHistoryLoading: t1,
    onNewChat,
    onItemClick,
    organizations,
    selectedOrganization,
    onSelectOrganization,
    currentItemId,
    className
  } = t0;
  const isHistoryLoading = t1 === undefined ? false : t1;
  let switcherOrganizations;
  let t2;
  if ($[0] !== organizations || $[1] !== selectedOrganization?.id) {
    switcherOrganizations = organizations.filter(_temp3);
    t2 = switcherOrganizations.find(org_0 => org_0.id === selectedOrganization?.id) ?? null;
    $[0] = organizations;
    $[1] = selectedOrganization?.id;
    $[2] = switcherOrganizations;
    $[3] = t2;
  } else {
    switcherOrganizations = $[2];
    t2 = $[3];
  }
  const switcherSelectedOrganization = t2;
  const listContainerRef = useRef(null);
  const router = useRouter();
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  let t3;
  if ($[4] !== posthog || $[5] !== router || $[6] !== signOut) {
    t3 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://lounge.llmgateway.io/login");
          }
        }
      });
    };
    $[4] = posthog;
    $[5] = router;
    $[6] = signOut;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const logout = t3;
  const renameItem = useRenameAudioHistory();
  const deleteItem = useDeleteAudioHistory();
  const [editingId, setEditingId] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [pendingFocusId, setPendingFocusId] = useState(null);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (id, prompt) => {
      setEditingId(id);
      setEditPrompt(prompt);
      setPendingFocusId(id);
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const startEdit = t4;
  let t5;
  if ($[9] !== editPrompt || $[10] !== renameItem) {
    t5 = (id_0, original) => {
      const next = editPrompt.trim();
      if (next && next !== original) {
        renameItem.mutate({
          params: {
            path: {
              id: id_0
            }
          },
          body: {
            prompt: next
          }
        });
      }
      setEditingId(null);
      setEditPrompt("");
      setPendingFocusId(null);
    };
    $[9] = editPrompt;
    $[10] = renameItem;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const saveEdit = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setEditingId(null);
      setEditPrompt("");
      setPendingFocusId(null);
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const cancelEdit = t6;
  let t7;
  if ($[13] !== deleteItem) {
    t7 = id_1 => {
      deleteItem.mutate({
        params: {
          path: {
            id: id_1
          }
        }
      });
    };
    $[13] = deleteItem;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleDeleteItem = t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setPendingFocusId(null);
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const onEditFocused = t8;
  let t9;
  if ($[16] !== galleryItems) {
    t9 = [...galleryItems].sort(_temp4);
    $[16] = galleryItems;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const sortedItems = t9;
  let t10;
  if ($[18] !== sortedItems) {
    t10 = groupItemsByDate(sortedItems);
    $[18] = sortedItems;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const itemGroups = t10;
  let rows;
  if ($[20] !== itemGroups.lastWeek || $[21] !== itemGroups.older || $[22] !== itemGroups.today || $[23] !== itemGroups.yesterday) {
    const groups = [{
      title: "Today",
      items: itemGroups.today
    }, {
      title: "Yesterday",
      items: itemGroups.yesterday
    }, {
      title: "Last 7 days",
      items: itemGroups.lastWeek
    }, {
      title: "Older",
      items: itemGroups.older
    }];
    rows = [];
    groups.forEach((t11, groupIndex) => {
      const {
        title,
        items: groupedItems
      } = t11;
      if (groupedItems.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedItems.forEach(item => {
        rows.push({
          type: "item",
          key: `item-${item.id}`,
          item
        });
      });
      const hasNextGroupWithItems = groups.slice(groupIndex + 1).some(_temp5);
      if (hasNextGroupWithItems) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[20] = itemGroups.lastWeek;
    $[21] = itemGroups.older;
    $[22] = itemGroups.today;
    $[23] = itemGroups.yesterday;
    $[24] = rows;
  } else {
    rows = $[24];
  }
  const historyRows = rows;
  let t11;
  if ($[25] !== currentItemId || $[26] !== editPrompt || $[27] !== editingId || $[28] !== handleDeleteItem || $[29] !== historyRows || $[30] !== onItemClick || $[31] !== pendingFocusId || $[32] !== saveEdit) {
    t11 = {
      rows: historyRows,
      currentItemId,
      editingId,
      editPrompt,
      pendingFocusId,
      onItemClick,
      onEditPromptChange: setEditPrompt,
      onSaveEdit: saveEdit,
      onCancelEdit: cancelEdit,
      onDeleteItem: handleDeleteItem,
      onStartEdit: startEdit,
      onEditFocused
    };
    $[25] = currentItemId;
    $[26] = editPrompt;
    $[27] = editingId;
    $[28] = handleDeleteItem;
    $[29] = historyRows;
    $[30] = onItemClick;
    $[31] = pendingFocusId;
    $[32] = saveEdit;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const rowProps = t11;
  const isAuthenticated = !!user;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t12;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4"><div className="flex w-full items-center gap-2"><ProductSwitcher /><Badge>Audio</Badge></div></div><StudioNav /></SidebarHeader>;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    let t13;
    if ($[35] !== className) {
      t13 = <Sidebar className={className}>{t12}</Sidebar>;
      $[35] = className;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    return t13;
  }
  if (!isAuthenticated) {
    let t12;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <div className="flex w-full items-center gap-2"><ProductSwitcher /><Badge>Audio</Badge></div>;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    let t13;
    let t14;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <div className="font-medium mb-2">Sign in required</div>;
      t14 = <p className="text-muted-foreground mb-3">Please sign in to generate audio.</p>;
      $[38] = t13;
      $[39] = t14;
    } else {
      t13 = $[38];
      t14 = $[39];
    }
    let t15;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    let t16;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t12}<div className="w-full rounded-md border p-4 text-sm">{t13}{t14}<div className="flex items-center justify-end gap-2">{t15}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div><StudioNav /></SidebarHeader>;
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    let t17;
    if ($[42] !== className) {
      t17 = <Sidebar className={className}>{t16}</Sidebar>;
      $[42] = className;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    return t17;
  }
  const t12 = (className ?? "") + " max-md:hidden";
  let t13;
  let t14;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SidebarMenuItem><ProductSwitcher /></SidebarMenuItem>;
    t14 = <SidebarChatSearch disabled={true} />;
    $[44] = t13;
    $[45] = t14;
  } else {
    t13 = $[44];
    t14 = $[45];
  }
  let t15;
  if ($[46] !== onNewChat) {
    t15 = <SidebarMenu>{t13}{t14}<SidebarNewAction label="New Generation" onAction={onNewChat} /></SidebarMenu>;
    $[46] = onNewChat;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <StudioNav />;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t15) {
    t17 = <SidebarHeader>{t15}{t16}</SidebarHeader>;
    $[49] = t15;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== onSelectOrganization || $[53] !== switcherOrganizations || $[54] !== switcherSelectedOrganization) {
    t19 = switcherOrganizations.length > 0 ? <SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={switcherOrganizations} selectedOrganization={switcherSelectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[52] = onSelectOrganization;
    $[53] = switcherOrganizations;
    $[54] = switcherSelectedOrganization;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t19) {
    t20 = <div>{t18}{t19}</div>;
    $[56] = t19;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== galleryItems || $[59] !== historyRows || $[60] !== isHistoryLoading || $[61] !== rowProps) {
    t21 = isHistoryLoading && galleryItems.length === 0 ? <HistorySkeleton withThumbnail={true} /> : galleryItems.length === 0 ? <div className="flex flex-col items-center justify-center py-8 text-center"><AudioLines className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">No generation history</p><p className="text-xs text-muted-foreground">Generate audio to see it here</p></div> : <List className="min-h-0 w-full flex-1" style={{
      width: "100%"
    }} rowComponent={AudioHistoryRowComponent} rowCount={historyRows.length} rowHeight={getAudioHistoryRowHeight} rowProps={rowProps} overscanCount={8} />;
    $[58] = galleryItems;
    $[59] = historyRows;
    $[60] = isHistoryLoading;
    $[61] = rowProps;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== isHistoryHidden || $[64] !== t21) {
    t22 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t21}</div>;
    $[63] = isHistoryHidden;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== t20 || $[67] !== t22) {
    t23 = <SidebarContent className="overflow-hidden pb-2">{t20}{t22}</SidebarContent>;
    $[66] = t20;
    $[67] = t22;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <SidebarLoungePoints />;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  const t25 = switcherSelectedOrganization ?? organization;
  const t26 = !switcherSelectedOrganization;
  let t27;
  if ($[70] !== isOrgLoading || $[71] !== t25 || $[72] !== t26) {
    t27 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t25} isLoading={isOrgLoading} isChatPlanOrg={t26} /></div>;
    $[70] = isOrgLoading;
    $[71] = t25;
    $[72] = t26;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  const t28 = user?.name ?? "User";
  let t29;
  if ($[74] !== user?.name) {
    t29 = user?.name?.split(" ").map(_temp6).join("").toUpperCase().slice(0, 2) ?? "U";
    $[74] = user?.name;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== t29) {
    t30 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t29}</span></div>;
    $[76] = t29;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  const t31 = user?.name;
  let t32;
  if ($[78] !== t31) {
    t32 = <span className="truncate font-semibold">{t31}</span>;
    $[78] = t31;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  const t33 = user?.email;
  let t34;
  if ($[80] !== t33) {
    t34 = <span className="truncate text-xs text-muted-foreground">{t33}</span>;
    $[80] = t33;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== t32 || $[83] !== t34) {
    t35 = <div className="grid flex-1 text-left text-sm leading-tight">{t32}{t34}</div>;
    $[82] = t32;
    $[83] = t34;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  let t36;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[85] = t36;
  } else {
    t36 = $[85];
  }
  let t37;
  if ($[86] !== t28 || $[87] !== t30 || $[88] !== t35) {
    t37 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t28} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t30}{t35}{t36}</SidebarMenuButton></DropdownMenuTrigger>;
    $[86] = t28;
    $[87] = t30;
    $[88] = t35;
    $[89] = t37;
  } else {
    t37 = $[89];
  }
  let t38;
  let t39;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <DropdownMenuItem asChild={true}><Link href="/pricing" prefetch={true}><CreditCard className="mr-2 h-4 w-4" />{"Membership & Billing"}</Link></DropdownMenuItem>;
    t39 = <DropdownMenuSeparator />;
    $[90] = t38;
    $[91] = t39;
  } else {
    t38 = $[90];
    t39 = $[91];
  }
  let t40;
  let t41;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t41 = <DropdownMenuSeparator />;
    $[92] = t40;
    $[93] = t41;
  } else {
    t40 = $[92];
    t41 = $[93];
  }
  let t42;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <span>Theme</span>;
    $[94] = t42;
  } else {
    t42 = $[94];
  }
  let t43;
  let t44;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp7}>{t42}<div onClick={_temp8} onKeyDown={_temp9}><ThemeToggle className="shrink-0" size="compact" /></div></DropdownMenuItem>;
    t44 = <DropdownMenuSeparator />;
    $[95] = t43;
    $[96] = t44;
  } else {
    t43 = $[95];
    t44 = $[96];
  }
  let t45;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <LogOut className="mr-2 h-4 w-4" />;
    $[97] = t45;
  } else {
    t45 = $[97];
  }
  let t46;
  if ($[98] !== logout) {
    t46 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t38}{t39}{t40}{t41}{t43}{t44}<DropdownMenuItem onClick={logout}>{t45}Log out</DropdownMenuItem></DropdownMenuContent>;
    $[98] = logout;
    $[99] = t46;
  } else {
    t46 = $[99];
  }
  let t47;
  if ($[100] !== t37 || $[101] !== t46) {
    t47 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t37}{t46}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[100] = t37;
    $[101] = t46;
    $[102] = t47;
  } else {
    t47 = $[102];
  }
  let t48;
  if ($[103] !== t27 || $[104] !== t47) {
    t48 = <SidebarFooter>{t24}{t27}{t47}</SidebarFooter>;
    $[103] = t27;
    $[104] = t47;
    $[105] = t48;
  } else {
    t48 = $[105];
  }
  let t49;
  if ($[106] !== t12 || $[107] !== t17 || $[108] !== t23 || $[109] !== t48) {
    t49 = <Sidebar collapsible="icon" className={t12}>{t17}{t23}{t48}</Sidebar>;
    $[106] = t12;
    $[107] = t17;
    $[108] = t23;
    $[109] = t48;
    $[110] = t49;
  } else {
    t49 = $[110];
  }
  return t49;
}
function _temp9(event_1) {
  return event_1.stopPropagation();
}
function _temp8(event_0) {
  return event_0.stopPropagation();
}
function _temp7(event) {
  return event.preventDefault();
}
function _temp6(n) {
  return n[0];
}
function _temp5(group) {
  return group.items.length > 0;
}
function _temp4(a, b) {
  return b.timestamp - a.timestamp;
}
function _temp3(org) {
  return org.kind === "default";
}